import styled from 'styled-components';
import { dataTestId } from 'modules/experiments/constants';
import {
  body1,
  colors,
  duration,
  easing,
  fadeIn,
  fadeOut,
  fontWeights,
  shadows,
  spacing,
} from 'ui/theme';
import { AiColor16 } from 'ui/icons';
import { InputText } from 'ui/inputs';
import { IconButton } from 'ui/button';
import { pxToRem } from 'utils/convert';
import { DeleteCardProgress } from 'modules/experiments/types';

type VariantActionButtonsProps = {
  isControl: boolean;
  isPreviewMode: boolean;
  isEditMode: boolean;
  isSendTimeExperiment: boolean;
  isViewOnlyPermission: boolean;
};

type VariantStateProps = VariantActionButtonsProps & {
  isDeleteInProgress: DeleteCardProgress;
  borderColor: string;
};

const customSpacing = {
  p40: pxToRem(40),
};

export const VariantCardWrapper = styled.div<VariantStateProps>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: ${spacing.base} ${spacing.plusHalf};

  animation-duration: ${({ isDeleteInProgress }) =>
    isDeleteInProgress === DeleteCardProgress.ENDED ? duration.long : duration.medium};
  animation-fill-mode: ${({ isDeleteInProgress }) =>
    isDeleteInProgress === DeleteCardProgress.ENDED ? 'forwards' : 'none'};
  animation-name: ${({ isDeleteInProgress }) =>
    isDeleteInProgress === DeleteCardProgress.ENDED ? fadeOut : fadeIn};
  animation-timing-function: ${({ isDeleteInProgress }) =>
    isDeleteInProgress === DeleteCardProgress.ENDED ? easing.exit : easing.entrance};

  background-color: ${({ isEditMode }) => (isEditMode ? colors.grey1 : colors.white)};
  outline: ${spacing.single} solid;
  border-radius: ${spacing.half};

  outline-color: ${({ isPreviewMode, isEditMode, borderColor }) => {
    switch (true) {
      case !isPreviewMode && !isEditMode:
        return colors.grey15;

      default:
        return borderColor;
    }
  }};

  &:hover {
    box-shadow: ${({ isPreviewMode, isEditMode }) =>
      isPreviewMode || isEditMode ? 'none' : shadows.medium.join(', ')};
    outline-width: ${({ isPreviewMode, isEditMode }) =>
      isPreviewMode || isEditMode ? spacing.single : spacing.eighth};

    [data-test=${dataTestId.variantActionButtons}] {
      visibility: ${({ isControl, isSendTimeExperiment, isViewOnlyPermission }) => {
        if (isViewOnlyPermission) return 'hidden';
        return isControl && !isSendTimeExperiment ? 'hidden' : 'visible';
      }};
    }
  }
`;

export const VariantHeaderWrapper = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  padding-bottom: ${spacing.base};
`;

export const VariantHeaderTitleWrapper = styled.div`
  display: flex;
  flex: 1 1 100%;
  align-items: center;
  gap: ${spacing.half};
  min-width: 0;
`;

export const VariantNameWrapper = styled.div`
  ${body1}
  display: flex;
  align-items: center;
  width: 100%;
  color: ${colors.textPrimary};
  font-weight: ${fontWeights.medium};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const EllipsisText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const VariantActionButtons = styled.div<VariantActionButtonsProps>`
  display: flex;
  min-width: ${spacing.x10};
  max-width: ${spacing.x10};
  justify-content: flex-end;
  visibility: ${({
    isEditMode,
    isControl,
    isPreviewMode,
    isSendTimeExperiment,
    isViewOnlyPermission,
  }) => {
    if (isViewOnlyPermission) return 'hidden';
    if (isControl && !isSendTimeExperiment) return 'hidden';
    return isEditMode || isPreviewMode ? 'visible' : 'hidden';
  }};
  button {
    &:focus:before {
      border: none;
    }
  }

  [data-test=${dataTestId.variantEditButton}] {
    visibility: ${({ isEditMode }) => (isEditMode ? 'hidden' : 'inherit')};
  }
`;

export const STOIcon = styled(AiColor16)`
  margin-left: ${spacing.half};
`;

export const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${colors.grey5};
  width: ${spacing.double};
  height: ${spacing.double};
`;

export const StyledVariantNameInput = styled(InputText)`
  border: none;
  background-color: inherit;
  padding: 0;
  :focus-within {
    box-shadow: none;
  }
  input {
    ${body1};
    padding: ${spacing.half} ${spacing.threeQuarter};
    background-color: inherit;
    border: ${spacing.eighth} solid ${colors.purple50};
    border-radius: ${spacing.threeEights};
    width: 100%;
    height: ${customSpacing.p40};
    color: ${colors.textPrimary};
    font-weight: ${fontWeights.medium};
    :focus {
      background: transparent;
    }
  }
`;

export const StyledVariantNameWrapper = styled.div`
  width: 100%;
`;

export const VariantEditbutton = styled(IconButton)`
  &:focus:before {
    border: none;
  }
`;
